/**
 * Redacted-output staleness for the audio/video flow (VE-25870).
 *
 * Decided by matching version numbers. Redact File tells the engine which
 * version to produce (`assetVersion`, postfixed onto the redacted asset) and
 * writes that same number onto the `redaction-media-data` the job consumes. The
 * verdict is then one comparison: does the newest save's `expectedRedactedVersion`
 * equal the newest output's `version`?
 *
 * Both sides carry the number by agreement, so neither has to observe the other.
 * That is what makes this immune to the order the assets happen to land in —
 * a save still uploading when the job is sent lands with the right label anyway.
 * An id-based check could not say that, and a timestamp-based one could not
 * either.
 *
 * Two records answer it, so both queries fetch `limit: 1`. A trimmed output
 * carries no version of its own — see `selectionsVersion` below — and borrows one
 * from the selections asset it names.
 *
 * Any newer save therefore makes the output stale, selection changes included —
 * only selected series reach the blur engine, so a save that changed the
 * selection changed the redacted file. Known limitation: select then unselect
 * leaves no net change and still warns.
 *
 * Outputs burned in before this scheme have no label to compare and fall back to
 * timestamps — a save that landed after the burn-in began means the output is
 * behind — anchored on the `redaction-user-selections` asset, which marks when
 * Redact File snapshotted, not on the output, which marks when the engine
 * finished. On a long video those are hours apart, and every save in between
 * would go unnoticed.
 *
 * That fallback is fenced two ways, because a false positive there is a warning
 * nobody can clear except by re-redacting: a minute of tolerance for the
 * pre-VE-25870 upload race, and a size ceiling above which the tolerance stops
 * meaning anything. Outside those bounds the file is left unjudged rather than
 * warned about on a guess. Nothing labelled ever reaches those fences — an
 * unburned save is answered outright.
 *
 * `tdoIsChanged` is the unsaved-edit signal, selection included, for the same
 * reason.
 *
 * Global settings feed the blur, so a settings save after the burn-in also makes
 * the output out of date. Same accepted simplification: a settings save that
 * changed nothing still counts.
 */
import { createSelector } from 'reselect';

import getLastRedactedFile from '@helpers/getLastRedactedFile';
import {
  computeRedactedFileStaleness,
  pickNewestBy,
  pickNewestRedactionData,
  readBurnInLabel,
  readRedactionProvenance,
} from '@helpers/redactedFileStaleness';
import {
  selectIsRedactJobStarting,
  selectRedactFiles,
} from '@common-modules/redactFile';

import { selectTdo, selectTdoIsChanged } from './general';
import { selectGlobalSettings } from './view';
import { selectRedactionRunning } from './tasks';

/**
 * A burn-in is in flight. Three signals because each covers a window the others
 * don't: the eager flag fires the instant REDACT FILE is clicked, the per-TDO
 * status covers from then until the engine registers a task, and running tasks
 * cover the rest. Without the middle one the banner blinks out while the job is
 * being created.
 */
export const selectIsRedactionInFlight = createSelector(
  selectRedactionRunning,
  selectIsRedactJobStarting,
  selectRedactFiles,
  selectTdo,
  (runningTasks, isRedactJobStarting, redactFiles, tdo) =>
    (runningTasks?.length ?? 0) > 0 ||
    !!isRedactJobStarting ||
    (!!tdo?.id && redactFiles[tdo.id]?.status === 'running')
);

/**
 * The output version the burn-in in flight is producing — the number behind
 * "Processing redacted version N" (VE-30516).
 *
 * N used to be `newest output + 1`, which is a different quantity: `assetVersion`
 * is `redactTasks.length + 1`, so it counts TASKS while the banner counted
 * OUTPUTS. Every failed burn-in widens the gap. On the file this was found on,
 * two attempts had failed and produced nothing, so the engine was told to make
 * version 3 while the banner announced version 1.
 *
 * Three sources, in descending order of directness:
 *
 * 1. The `redactFile` duck, where the redact saga parks `assetVersion` at the
 *    click. Exact, and the only source during the run itself — the selections
 *    asset below is written by the worker but does not reach this store until
 *    `FETCH_REDACTED_MEDIA_SUCCESS`, which lands when the run FINISHES.
 * 2. The newest `redaction-user-selections` asset, which Redact File stamps with
 *    the same number. The duck is memory-only, so this is what answers after a
 *    reload mid-burn-in. The newest asset is the right one to read because a
 *    second burn-in cannot be started while one is running — REDACT FILE is
 *    disabled for the duration — so the newest snapshot is the running job's.
 *    Deliberately NOT `consumedSelections`, which is matched by the id the
 *    FINISHED output names and therefore describes the previous run.
 * 3. Neither, for a file predating these labels — the caller falls back to
 *    `newest output + 1`, the one case that was ever right.
 */
export const selectInFlightRedactedVersion = createSelector(
  selectTdo,
  selectRedactFiles,
  selectIsRedactionInFlight,
  (tdo, redactFiles, isInFlight): number | undefined => {
    // Nothing running, nothing to name. Without this the selector answers with
    // the LAST run's version whenever it is asked outside a burn-in — correct
    // only because its one caller happens to sit inside `isRedactionRunning &&`,
    // which is a trap for the next one.
    if (!isInFlight) {
      return undefined;
    }
    const parked = tdo?.id ? redactFiles[tdo.id]?.version : undefined;
    if (typeof parked === 'number') {
      return parked;
    }

    const newestSelections = pickNewestBy(
      tdo?.redactionSelectionAssets?.records,
      (record) => record.createdDateTime
    );
    // `details` is an untyped JSON blob; older assets carry no version.
    const snapshotVersion: unknown =
      newestSelections?.details?.expectedRedactedVersion;
    return typeof snapshotVersion === 'number' ? snapshotVersion : undefined;
  }
);

/**
 * Slack on the legacy comparison below. Before VE-25870, Redact File fired the
 * save and the burn-in as two unsequenced dispatches: both bodies start
 * uploading in the same tick and either can land first, so a `redaction-media-data`
 * asset written within a minute of the selections asset is that race, not a later
 * edit. Its contents are in the output regardless — the selections body carried
 * them from memory. A minute is roughly three times the worst realistic gap (the
 * save's three retries at 3s, plus the attempts themselves).
 */
const LEGACY_BURN_IN_RACE_TOLERANCE_MS = 60_000;

/**
 * Above this, the tolerance stops being trustworthy: a body large enough to take
 * minutes to upload could land well after the selections asset and still belong
 * to the same Redact File click. Rather than widen the tolerance for everyone,
 * files that big skip the legacy comparison entirely and stay quiet.
 *
 * The size checked is the newest save's, which is the right one: a false positive
 * can only arise when the racing save IS the newest — any later save is a genuine
 * post-burn-in edit and should warn whatever it weighs.
 */
const RACE_SAFE_ASSET_BYTES = 30 * 1024 * 1024;

/** Under the size gate, and known — an absent size is not treated as small. */
const isRaceSafeSize = (
  asset: { fileData?: { size?: number | null } } | null
) =>
  typeof asset?.fileData?.size === 'number' &&
  asset.fileData.size < RACE_SAFE_ASSET_BYTES;

/**
 * The legacy verdict: a save landed after this burn-in began. Reached only for
 * saves carrying no version label, and only where both assets are small enough
 * for the race tolerance to mean anything.
 */
const savedAfterLegacyBurnIn = (
  newestSave: {
    createdDateTime?: string;
    fileData?: { size?: number | null };
  } | null,
  consumedSelections:
    | { createdDateTime?: string; fileData?: { size?: number | null } }
    | undefined
): boolean => {
  if (
    !newestSave?.createdDateTime ||
    !consumedSelections?.createdDateTime ||
    !isRaceSafeSize(newestSave) ||
    !isRaceSafeSize(consumedSelections)
  ) {
    return false;
  }
  return (
    new Date(newestSave.createdDateTime).getTime() >
    new Date(consumedSelections.createdDateTime).getTime() +
      LEGACY_BURN_IN_RACE_TOLERANCE_MS
  );
};

export const selectRedactedFileStaleness = createSelector(
  selectTdo,
  selectIsRedactionInFlight,
  selectTdoIsChanged,
  selectGlobalSettings,
  (tdo, isRedactionInFlight, tdoIsChanged, globalSettings) => {
    const lastRedactedFile = getLastRedactedFile(tdo);
    // `details` is an untyped JSON blob and older assets predate `version`.
    const version: unknown = lastRedactedFile?.details?.version;

    // The selections asset the burn-in consumed, matched by the id the output
    // names rather than by recency: Redact File writes that asset just before
    // sending the job, so the newest one can belong to a later run that failed or
    // is still going. Only the legacy comparison needs it.
    const provenance = readRedactionProvenance(lastRedactedFile?.details);
    const consumedSelections = provenance.redactionAssetId
      ? tdo?.redactionSelectionAssets?.records?.find(
          (record) => record.id === provenance.redactionAssetId
        )
      : undefined;

    const newestSave = pickNewestRedactionData(
      tdo?.redactionDataAssets?.records
    );
    const label = readBurnInLabel(newestSave?.details);

    // The engine deliberately omits `version` from a trimmed `redacted-media-clip`:
    // the number is a position in the patch chain (each version is produced by
    // patching the one before it), and a clip can neither be patched from nor
    // patched onto, so it has no place in that sequence. The selections asset the
    // clip names carries the version Redact File asked for, which is what we
    // actually want to know, so it stands in. Without it a trimmed output has no
    // version at all: the verdict falls through to the legacy timestamp fences,
    // and the processing banner counts up from zero and announces version 1.
    // `details` is an untyped JSON blob, same as the output's above.
    const selectionsVersion: unknown =
      consumedSelections?.details?.expectedRedactedVersion;
    const outputVersion =
      typeof version === 'number'
        ? version
        : typeof selectionsVersion === 'number'
          ? selectionsVersion
          : undefined;

    /**
     * Is there saved redaction data the output does not contain? Three ways to
     * answer, in descending order of certainty.
     */
    const outputIsBehind = (() => {
      // The newest save was sent for burn-in, and says which output version it
      // was sent to produce. It is current exactly when that output is the one
      // we have.
      if (label.state === 'burnedIn' && outputVersion !== undefined) {
        return label.version !== outputVersion;
      }
      // An ordinary save, never sent for burn-in, so by definition not in the
      // output. Certain — and deliberately not routed through the legacy
      // comparison below, whose tolerance and size ceiling would forgive it for
      // reasons that only apply to assets written before any of this existed.
      if (label.state === 'saved') {
        return true;
      }
      // The output names a selections asset that isn't the one on hand: a later
      // Redact File superseded it or failed, and either way the file is behind.
      if (provenance.redactionAssetId && !consumedSelections) {
        return true;
      }
      return savedAfterLegacyBurnIn(newestSave, consumedSelections);
    })();

    // The moment the settings had to be in place by is when Redact File
    // snapshotted the selections, not when the burn-in finished — on a long video
    // those are hours apart, and a settings save in between did not reach the
    // engine. Falls back to the output's own timestamp when the selections asset
    // is out of reach.
    const settingsSavedAt = globalSettings.lastSavedDateTime;
    const burnedInAt =
      consumedSelections?.createdDateTime ??
      lastRedactedFile?.createdDateTime ??
      lastRedactedFile?.modifiedDateTime;
    const settingsChangedAfterBurnIn =
      !!settingsSavedAt &&
      !!burnedInAt &&
      new Date(settingsSavedAt).getTime() > new Date(burnedInAt).getTime();

    return computeRedactedFileStaleness({
      hasOutput: !!lastRedactedFile,
      savedAfterBurnIn: outputIsBehind,
      hasUnsavedChanges: !!tdoIsChanged,
      settingsChangedAfterBurnIn,
      isBurnInRunning: isRedactionInFlight,
      version: outputVersion,
    });
  }
);
